import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Typography,
  Box,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert
} from '@mui/material';
import {
  Sort as SortIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  WaterDrop as WaterDropIcon,
  Percent as PercentIcon,
  SortByAlpha as SortByAlphaIcon,
  DateRange as DateRangeIcon,
  Close as CloseIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const SortDropdown = ({ open, onClose, onSortApply, selectedProductId }) => {
  const [selectedSort, setSelectedSort] = React.useState('');

  // 「すべて」が選択されているかどうか
  const isAllSelected = selectedProductId === 'all';

  // 残量・割合ソートのみ
  const sortOptions = [
    {
      category: '残量・割合並び替え',
      options: [
        {
          value: 'remaining_asc',
          label: '残量（少ない順）',
          icon: <WaterDropIcon sx={{ color: '#f44336' }} />,
          description: '残量が少ない順番で表示（緊急度順）'
        },
        {
          value: 'remaining_desc',
          label: '残量（多い順）',
          icon: <WaterDropIcon sx={{ color: '#4caf50' }} />,
          description: '残量が多い順番で表示'
        },
        {
          value: 'ratio_asc',
          label: '残量割合（低い順）',
          icon: <PercentIcon sx={{ color: '#f44336' }} />,
          description: '残量割合が低い順番で表示（緊急度順）'
        },
        {
          value: 'ratio_desc',
          label: '残量割合（高い順）',
          icon: <PercentIcon sx={{ color: '#4caf50' }} />,
          description: '残量割合が高い順番で表示'
        }
      ]
    }
  ];

  const handleSortChange = (value) => {
    setSelectedSort(value);
  };

  // 🚀 ソート適用処理（新しい状態管理に対応）
  const handleApply = useCallback(() => {
    if (selectedSort) {
      onSortApply(selectedSort);
      onClose(); // モーダルを閉じる
    }
  }, [selectedSort, onSortApply, onClose]);

  // 🚀 リセット処理（新しい状態管理に対応）
  const handleReset = useCallback(() => {
    setSelectedSort('');
    onSortApply('id_asc'); // デフォルトソートに戻す
    onClose(); // モーダルを閉じる
  }, [onSortApply, onClose]);

  // モーダル閉じ処理の最適化
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: '95vh', sm: '90vh' }
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SortIcon color="primary" />
          <Typography variant="h6" fontWeight="medium">
            並び替え設定
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          顧客一覧の表示順序を選択してください
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, px: { xs: 2, sm: 3 } }}>
        {/* 「すべて」選択時の警告表示 */}
        {isAllSelected && (
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ mb: 2 }}
          >
            「すべて表示」選択時は並び替え機能は利用できません。特定の製品を選択すると、残量データによる並び替えが可能になります。
          </Alert>
        )}

        <RadioGroup
          value={selectedSort}
          onChange={(e) => handleSortChange(e.target.value)}
        >
          {sortOptions.map((category, categoryIndex) => (
            <Box key={category.category}>
              <Typography 
                variant="subtitle2" 
                color={isAllSelected ? "text.disabled" : "primary"}
                sx={{ 
                  mt: categoryIndex > 0 ? 2 : 0, 
                  mb: 1, 
                  fontWeight: 'medium',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                {category.category}
              </Typography>
              
              {category.options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio disabled={isAllSelected} />}
                  disabled={isAllSelected}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    ml: 0,
                    mr: 0,
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid transparent',
                    transition: 'all 0.2s ease-in-out',
                    opacity: isAllSelected ? 0.5 : 1,
                    '&:hover': {
                      bgcolor: isAllSelected ? 'transparent' : 'grey.50',
                      borderColor: isAllSelected ? 'transparent' : 'grey.300'
                    },
                    '&.Mui-checked': {
                      bgcolor: isAllSelected ? 'transparent' : 'primary.50',
                      borderColor: isAllSelected ? 'transparent' : 'primary.main'
                    }
                  }}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Box sx={{ minWidth: 24, opacity: isAllSelected ? 0.5 : 1 }}>
                        {option.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="body1" 
                          fontWeight="medium"
                          color={isAllSelected ? "text.disabled" : "text.primary"}
                          sx={{
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }}
                        >
                          {option.label}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color={isAllSelected ? "text.disabled" : "text.secondary"}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.8125rem' }
                          }}
                        >
                          {option.description}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              ))}
              
              {categoryIndex < sortOptions.length - 1 && (
                <Divider sx={{ my: 1 }} />
              )}
            </Box>
          ))}
        </RadioGroup>

        {/* プレビューセクション */}
        {selectedSort && !isAllSelected && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              選択中の並び替え:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {sortOptions
                .flatMap(cat => cat.options)
                .find(opt => opt.value === selectedSort)?.icon}
              <Typography variant="body2">
                {sortOptions
                  .flatMap(cat => cat.options)
                  .find(opt => opt.value === selectedSort)?.label}
              </Typography>
            </Box>
          </Box>
        )}

        {/* 注意事項 */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
          <Typography variant="caption" color="info.main">
            <strong>※ 残量・割合での並び替えについて:</strong><br />
            {isAllSelected ? (
              '特定の製品を選択すると、その製品の残量データによる並び替えが可能になります。'
            ) : (
              '選択された製品の残量データに基づいて並び替えを行います。残量データが未設定の顧客は最後に表示されます。'
            )}
          </Typography>
        </Box>
      </DialogContent>

      {/* ボタン折り返し防止の堅牢なレイアウト */}
      <DialogActions 
        sx={{ 
          px: { xs: 2, sm: 3 }, 
          pb: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' }, // 小画面では縦並び
          gap: { xs: 1, sm: 1 },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: { xs: 'stretch', sm: 'flex-end' }
        }}
      >
        {/* レスポンシブボタングループ */}
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' }, // 小画面では逆順（適用が上に）
            gap: 1,
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Button
            onClick={handleReset}
            startIcon={<CloseIcon />}
            disabled={!selectedSort || isAllSelected}
            size="small"
            sx={{
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              px: { xs: 2, sm: 2 },
              whiteSpace: 'nowrap',
              minWidth: { xs: 'auto', sm: '80px' }
            }}
          >
            リセット
          </Button>
          <Button
            onClick={handleClose}
            color="inherit"
            size="small"
            sx={{
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              px: { xs: 2, sm: 2 },
              whiteSpace: 'nowrap',
              minWidth: { xs: 'auto', sm: '80px' }
            }}
          >
            {isAllSelected ? '閉じる' : 'キャンセル'}
          </Button>
          {!isAllSelected && (
            <Button
              onClick={handleApply}
              variant="contained"
              startIcon={<SortIcon />}
              disabled={!selectedSort}
              size="small"
              sx={{
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                px: { xs: 2, sm: 2 },
                whiteSpace: 'nowrap',
                minWidth: { xs: 'auto', sm: '80px' },
                fontWeight: 600 // 適用ボタンを強調
              }}
            >
              適用
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SortDropdown;